import { Add } from "@mui/icons-material";
import { Box, Button, Card, styled, useTheme, Grid } from "@mui/material";
import LocationCityIcon from '@mui/icons-material/LocationCity';
import Public from '@mui/icons-material/Public';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import CurrencyRupee from '@mui/icons-material/CurrencyRupee';
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { H6 } from "components/Typography";
import DataTable from "page-sections/data-table/dataTableV2/DataTable";
import LeadCard from "page-sections/dashboards/CRM/LeadCard";
import LongCard from "page-sections/dashboards/CRM/LongCard";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import AddDistrictModal from "page-sections/data-table/dataTableV2/AddDistrictModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { usePageTitle } from "contexts/PageTitleContext";
import { Typography } from "@mui/material";

const ButtonWrapper = styled(FlexBox)(({
  theme
}) => ({
  [theme.breakpoints.down(500)]: {
    marginTop: 10,
    width: "100%",
    flexDirection: "column-reverse",
    "& > .MuiBox-root": {
      width: "100%",
      margin: "10px 0",
      alignItems: "center",
      flexDirection: "column"
    },
    "& .MuiButton-root": {
      minWidth: "100%"
    }
  }
}));

const DatePickerWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  "& .react-datepicker-wrapper": {
    width: "130px"
  },
  "& .react-datepicker__input-container input": {
    padding: "8px 12px",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    width: "100%",
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover': {
      borderColor: theme.palette.primary.main
    },
    '&:focus': {
      borderColor: theme.palette.primary.main,
      outline: 'none',
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`
    }
  },
  "& .react-datepicker": {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    fontFamily: theme.typography.fontFamily,
    "& .react-datepicker__header": {
      backgroundColor: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
      "& .react-datepicker__current-month": {
        color: theme.palette.text.primary,
        fontSize: "0.875rem",
        fontWeight: theme.typography.fontWeightMedium
      },
      "& .react-datepicker__day-name": {
        color: theme.palette.text.secondary
      }
    },
    "& .react-datepicker__day": {
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.palette.action.hover
      },
      "&.react-datepicker__day--selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark
        }
      },
      "&.react-datepicker__day--keyboard-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
      },
      "&.react-datepicker__day--disabled": {
        color: theme.palette.text.disabled
      }
    },
    "& .react-datepicker__triangle": {
      display: "none"
    }
  }
}));

const SummaryText = styled(Typography)(({ theme }) => ({
  margin: '24px 0',
  padding: '16px 24px',
  background: `linear-gradient(to right, ${theme.palette.primary.light}15, ${theme.palette.background.paper})`,
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  borderRadius: '4px',
  color: theme.palette.text.primary,
  fontSize: '1rem',
  fontWeight: 400,
  display: 'block',
  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
    transform: 'translateY(-1px)',
  },
  '& .date': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    position: 'relative',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: -2,
      left: 0,
      width: '100%',
      height: '2px',
      background: `${theme.palette.primary.main}25`,
    }
  }
}));

const DataTableV2 = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setPageTitle } = usePageTitle();
  const [districts, setDistricts] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)));
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    setPageTitle('District Management');
}, [setPageTitle]);

  const fetchDistrictSummary = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_district_summery}?start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      );
      
      if (response?.success) {
        setDistricts(response.data);
        setSummaryTotals(response.totals);
      }
    } catch (error) {
      console.error("Error fetching district summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDistrictSummary();
  }, [startDate, endDate]);

  const summaryList = summaryTotals ? [
    {
      id: 1,
      amount: summaryTotals.total_districts,
      Icon: LocationCityIcon,
      title: "Total Districts",
      color: theme.palette.primary.main
    },
    {
      id: 2,
      amount: summaryTotals.total_regions,
      Icon: Public,
      title: "Total Regions",
      color: theme.palette.info.main
    },
    {
      id: 3,
      amount: summaryTotals.total_chapters,
      Icon: LibraryBooks,
      title: "Total Chapters",
      color: theme.palette.text.disabled
    }
  ] : [];

  return (
    <Box pt={2} pb={4}>
      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item md={4} xs={12}>
          <LeadCard businessDone={summaryTotals?.total_business_done || 0} />
        </Grid>

        <Grid item md={8} xs={12}>
          <LongCard list={summaryList} />
        </Grid>
      </Grid>

      {/* Table Controls */}
      <FlexBetween flexWrap="wrap" mb={2}>
        <FlexBox alignItems="center" gap={3}>
          <SearchInput 
            bordered={false} 
            placeholder="Find Districts" 
            onChange={e => setSearchValue(e.target.value)} 
          />
          
          <DatePickerWrapper>
            <H6>From:</H6>
            <DatePicker
              selected={startDate}
              onChange={date => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate}
            />
            <H6>To:</H6>
            <DatePicker
              selected={endDate}
              onChange={date => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
            />
          </DatePickerWrapper>
        </FlexBox>

        <ButtonWrapper alignItems="center">
          <Button 
            variant="contained" 
            size="small" 
            endIcon={<Add />} 
            onClick={() => setOpenModal(true)}
          >
            {t("Add District")}
          </Button>
        </ButtonWrapper>
      </FlexBetween>

      <AddDistrictModal 
        open={openModal} 
        onClose={() => setOpenModal(false)}
        onSuccess={fetchDistrictSummary}
      />
      <SummaryText>
        Summary of Districts from{' '}
        <span className="date">
          {startDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </span>{' '}
        to{' '}
        <span className="date">
          {endDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </span>
      </SummaryText>

      <Card sx={{ marginTop: 3 }}>
        <DataTable 
          data={districts} 
          searchQuery={searchValue} 
          isLoading={isLoading} 
          onRefresh={fetchDistrictSummary} 
          startDate={startDate}
          endDate={endDate}
        />
      </Card>
    </Box>
  );
};

export default DataTableV2;